import type { GameEvent } from "./game";

const BAR_ROOTS = [48, 48, 53, 55, 48, 53, 55, 48, 48, 45, 53, 55, 48, 45, 53, 55, 45, 53, 48, 55, 45, 53, 50, 55, 53, 55, 45, 48, 53, 55, 48, 48];
const MINOR_BARS = new Set([9, 13, 16, 20, 22, 26]);
const LEADS: Array<Array<number | null>> = [
  [6, null, null, null, 8, null, null, null, 6, null, 9, null, 7, null, null, null],
  [7, null, 8, 9, 8, 7, 6, null, 6, null, 7, 8, 7, 6, null, null],
  [6, 7, 8, null, 9, 8, 7, null, 6, 7, 8, 7, 6, null, 9, 6],
  [6, 7, 8, 9, 8, 7, null, 6, 8, 7, 6, null, 9, 7, 8, 6],
];
const BASSES: Array<Array<number | null>> = [
  [0, null, null, null, 2, null, null, null, 0, null, null, null, 2, null, null, null],
  [0, null, 3, 2, 0, null, 3, null, 0, 2, null, 3, 0, null, 2, null],
  [0, 3, null, 2, 1, null, 2, null, 0, 2, 3, null, 0, 1, 2, null],
  [0, 3, null, 2, 0, null, 3, 2, 0, 2, null, 3, 1, null, 2, null],
];
const MAJOR = [0, 4, 7, 12, 16, 19, 24, 28, 31, 26];
const MINOR = [0, 3, 7, 12, 15, 19, 24, 27, 31, 26];

function frequency(midi: number): number {
  return 440 * 2 ** ((midi - 69) / 12);
}

export class ArcadeAudio {
  private context: AudioContext | null = null;
  private timer: number | null = null;
  private nextAt = 0;
  private step = 0;

  async start(): Promise<void> {
    if (!this.context) this.context = new AudioContext();
    await this.context.resume();
    if (this.timer !== null) return;
    this.nextAt = this.context.currentTime + 0.03;
    this.timer = window.setInterval(() => this.schedule(), 40);
    this.schedule();
  }

  stop(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
    this.step = 0;
    const context = this.context;
    this.context = null;
    void context?.close();
  }

  effect(event: GameEvent): void {
    if (!this.context) return;
    const notes: Record<GameEvent, number[]> = {
      wall: [300], paddle: [520], point: [220, 150],
      win: [440, 660, 880], loss: [330, 220, 110],
    };
    let at = this.context.currentTime;
    for (const note of notes[event]) {
      this.voice(note, at, event === "point" ? 0.07 : 0.045, 0.075, "square");
      at += 0.055;
    }
  }

  private schedule(): void {
    if (!this.context) return;
    while (this.nextAt < this.context.currentTime + 0.18) {
      const section = Math.floor(this.step / 128);
      const bar = Math.floor(this.step / 16);
      const beat = this.step % 16;
      const root = BAR_ROOTS[bar];
      const tones = MINOR_BARS.has(bar) ? MINOR : MAJOR;
      const lead = LEADS[section][beat];
      const bass = BASSES[section][beat];
      if (lead !== null) this.voice(frequency(root + tones[lead]), this.nextAt, 0.078, 0.022, "square");
      if (bass !== null) {
        const bassFrequency = frequency(root + tones[bass]);
        this.voice(bassFrequency, this.nextAt, 0.086, 0.045, "sawtooth");
        this.voice(bassFrequency / 2, this.nextAt, 0.086, 0.022, "square");
      }
      this.nextAt += 0.105;
      this.step = (this.step + 1) % 512;
    }
  }

  private voice(frequencyHz: number, at: number, duration: number, gainValue: number, type: OscillatorType): void {
    if (!this.context) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequencyHz;
    gain.gain.setValueAtTime(gainValue, at);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start(at);
    oscillator.stop(at + duration);
  }
}
